import type { Project } from '@servicetim/shared';
import {
  useCopyPermissionScheme,
  usePermissionScheme,
  usePermissionSchemes,
  useSwitchProjectPermissionScheme,
} from '@servicetim/ui/api';
import {
  Badge,
  Button,
  buttonVariants,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ProblemBanner,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { Link } from '@tanstack/react-router';
import { Copy, ExternalLink } from 'lucide-react';
import { useId, useState } from 'react';
import { FormRow } from '../ui/form-row';
import { Section } from '../ui/section';
import { SimpleSelect } from '../ui/simple-select';

/** Current permission scheme with a link to its matrix, a switch select and copy-into-own. */
export function SchemePanel({ project }: { project: Project }) {
  const id = useId();
  const current = usePermissionScheme(project.permissionSchemeId ?? undefined);
  const schemes = usePermissionSchemes(200);
  const switchScheme = useSwitchProjectPermissionScheme(project.id);
  const copyScheme = useCopyPermissionScheme();
  const confirm = useConfirm();
  const [target, setTarget] = useState<string | undefined>(undefined);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyName, setCopyName] = useState(`${project.key} scheme`);

  const onSwitch = async () => {
    if (!target || target === project.permissionSchemeId) return;
    const name = schemes.items.find((s) => s.id === target)?.name ?? 'the selected scheme';
    const ok = await confirm({
      title: `Switch ${project.key} to "${name}"?`,
      description:
        'Permissions take effect immediately for everyone in the project; the configuration version moves on.',
      confirmLabel: 'Switch scheme',
      onConfirm: () => switchScheme.mutateAsync({ permissionSchemeId: target }),
    });
    if (ok) {
      toast.success(`Permission scheme switched to "${name}"`);
      setTarget(undefined);
    }
  };

  const onCopy = async () => {
    if (!project.permissionSchemeId || copyName.trim().length === 0) return;
    try {
      const copy = await copyScheme.mutateAsync({
        id: project.permissionSchemeId,
        name: copyName.trim(),
      });
      await switchScheme.mutateAsync({ permissionSchemeId: copy.id });
      toast.success(`"${copy.name}" created and assigned to ${project.key}`);
      setCopyOpen(false);
    } catch {
      // banner
    }
  };

  const currentDetail = current.data;
  return (
    <div className="space-y-6">
      <Section
        title="Current scheme"
        description="Permission schemes are shared: editing the matrix changes every project using it."
        actions={
          project.permissionSchemeId ? (
            <Link
              to="/permission-schemes/$schemeId"
              params={{ schemeId: project.permissionSchemeId }}
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
            >
              <ExternalLink />
              Open matrix
            </Link>
          ) : null
        }
      >
        {project.permissionSchemeId === null ? (
          <p className="text-fg-muted text-sm">This project has no permission scheme.</p>
        ) : current.error ? (
          <ProblemBanner error={current.error} size="sm" />
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-medium text-fg">
              {currentDetail?.name ?? <span className="text-fg-muted">Loading…</span>}
            </span>
            {currentDetail?.projectCount !== undefined ? (
              <Badge variant="secondary">
                used by {currentDetail.projectCount} project
                {currentDetail.projectCount === 1 ? '' : 's'}
              </Badge>
            ) : null}
            {currentDetail ? (
              <Badge variant="outline">{currentDetail.grants.length} grants</Badge>
            ) : null}
            {currentDetail?.description ? (
              <p className="w-full text-fg-muted text-sm">{currentDetail.description}</p>
            ) : null}
          </div>
        )}
      </Section>

      <Section title="Switch scheme" description="Point this project at another existing scheme.">
        {schemes.error ? <ProblemBanner error={schemes.error} size="sm" /> : null}
        <div className="flex flex-wrap items-end gap-2">
          <FormRow label="Scheme" htmlFor={`${id}-scheme`} className="min-w-64 flex-1">
            <SimpleSelect
              id={`${id}-scheme`}
              value={target}
              onChange={setTarget}
              options={schemes.items.map((s) => ({
                value: s.id,
                label:
                  s.id === project.permissionSchemeId
                    ? `${s.name} (current)`
                    : s.projectCount !== undefined
                      ? `${s.name} · ${s.projectCount} project${s.projectCount === 1 ? '' : 's'}`
                      : s.name,
                disabled: s.id === project.permissionSchemeId,
              }))}
              placeholder={schemes.isPending ? 'Loading schemes…' : 'Choose a scheme…'}
              disabled={schemes.isPending || schemes.error !== null}
            />
          </FormRow>
          <Button onClick={() => void onSwitch()} disabled={!target || switchScheme.isPending}>
            Switch
          </Button>
        </div>
      </Section>

      <Section
        title="Copy into a project-specific scheme"
        description="Duplicate the current matrix so changes here stop affecting other projects."
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setCopyName(`${project.key} scheme`);
              copyScheme.reset();
              setCopyOpen(true);
            }}
            disabled={!project.permissionSchemeId}
          >
            <Copy />
            Copy scheme
          </Button>
        }
      >
        <p className="text-fg-muted text-sm">
          The copy starts identical and is assigned to this project right away.
        </p>
      </Section>

      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Copy permission scheme</DialogTitle>
            <DialogDescription>
              Creates a copy of "{currentDetail?.name ?? 'the current scheme'}" and switches this
              project to it.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void onCopy();
            }}
          >
            {copyScheme.error ? <ProblemBanner error={copyScheme.error} size="sm" /> : null}
            <FormRow label="Name of the copy" htmlFor={`${id}-copy-name`} required>
              <Input
                id={`${id}-copy-name`}
                value={copyName}
                onChange={(e) => setCopyName(e.target.value)}
                maxLength={80}
                autoFocus
              />
            </FormRow>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCopyOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={copyScheme.isPending || switchScheme.isPending || copyName.trim() === ''}
              >
                Copy and assign
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
